import { cached } from '@glimmer/tracking';

import type { DSSetup } from 'webapp-types';
import { CompareType, GoalStatus } from 'webapp/enums/enums';
import { getChangeDirection } from 'webapp/utils/data';

import DateRange from 'webapp/models-custom/date-range';
import DsCompare from 'webapp/models-custom/ds-compare';
import type DsDataPointCompare from 'webapp/models-custom/ds-data/point/compare';
import type DsDataPointOther from 'webapp/models-custom/ds-data/point/other';
import type { PointBaseData } from 'webapp/d-chart/point/point-base';
import DChartPointBase from 'webapp/d-chart/point/point-base';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

export type ComparePointData = PointBaseData & {
	primaryPoint: DChartPrimaryPoint;
	compareSettings?: DSSetup.Compare | null; // might not be full formula
	change?: number | null;
	changeStr?: string;
	indicatorClass?: string;
	compareIndex?: number;
	metricName?: string;
};

export default class DChartComparePoint extends DChartPointBase {
	primaryPoint: DChartPrimaryPoint;
	compareSettings: DSSetup.Compare;
	change: number;
	changeStr: string;
	private _indicatorClass: string;
	compareIndex: number; // index of the compare on the DS
	metricName: string; // used for compareText when comparing vs other metric

	constructor(pointData: ComparePointData) {
		super(pointData);

		this.primaryPoint = pointData.primaryPoint;

		this.changeStr = pointData.changeStr || '';
		this._indicatorClass = pointData.indicatorClass || '';
		this.metricName = pointData.metricName || '';

		this.compareIndex = pointData.compareIndex || 0;

		if (pointData.compareSettings) {
			this.compareSettings = pointData.compareSettings;
		} else {
			this.compareSettings = { type: CompareType.RANGE };
		}

		if (typeof pointData.change === 'number') {
			this.change = pointData.change;
		} else {
			// calculate?
			this.change = 0;
		}
	}

	get primaryDateRange() {
		return this.primaryPoint.dateRange;
	}

	get compareType() {
		return this.compareSettings.type;
	}

	get hasValue() {
		return typeof this.value === 'number';
	}

	@cached
	get compareText(): string {
		const isFullCompareSettings =
			!!this.compareSettings.dateRange || (!this.isRangeCompare && !this.isOneOfForecasts);
		if (isFullCompareSettings) {
			const dateRange = this.primaryDateRange
				? DateRange.createFromFormula(this.primaryDateRange)
				: undefined;
			const compare = DsCompare.createFromFormula(this.compareSettings, dateRange);
			if ((compare.isOtherMetric || compare.isGoal) && this.metricName) {
				// compare.titleVs reads from the metric name, which we might not have here (e.g. public url)
				// so we read from the data here instead
				return `vs ${this.metricName}`;
			} else {
				return compare.titleVs;
			}
		} else if (this.isOneOfForecasts) {
			return 'vs forecast';
		} else {
			return 'vs comparison period';
		}
	}

	get timeLabel(): string {
		return this.granulationLabel.label;
	}

	get timeLabelLong(): string {
		return this.granulationLabel.labelLong;
	}

	get arrowDirection() {
		return getChangeDirection(this.changeStr);
	}

	get tooltipTypeClass() {
		return this.primaryPoint.tooltipTypeClass;
	}

	get isRangeCompare() {
		return this.compareType === CompareType.RANGE;
	}

	get isAnomaly() {
		return this.compareType === CompareType.ANOMALY;
	}

	get isGoal() {
		return this.compareType === CompareType.GOAL;
	}

	get indicatorClass(): string {
		switch (this.goalData?.goalStatus) {
			case GoalStatus.ON_TRACK:
			case GoalStatus.ACHIEVED:
				return 'goal-done';
			case GoalStatus.AT_RISK:
			case GoalStatus.PARTIAL:
				return 'goal-medium';
			case GoalStatus.OFF_TRACK:
			case GoalStatus.MISSED:
				return 'goal-low';
			default:
				return this._indicatorClass;
		}
	}

	get isForecast() {
		return this.compareType === CompareType.FORECAST;
	}

	get isForecastOptimistic() {
		return this.compareType === CompareType.FORECAST_OPTIMISTIC;
	}

	get isForecastPessimistic() {
		return this.compareType === CompareType.FORECAST_PESSIMISTIC;
	}

	get isOneOfForecasts(): boolean {
		return this.isForecast || this.isForecastOptimistic || this.isForecastPessimistic;
	}

	static createFromOutputPointCompare(
		outputPointCompare: DsDataPointCompare,
		primaryPoint: DChartPrimaryPoint,
	): DChartComparePoint {
		return new DChartComparePoint({
			primaryPoint,
			value: outputPointCompare.value,
			valueStr: outputPointCompare.valueStr,
			compareSettings: outputPointCompare.compareSettings,
			change: outputPointCompare.change,
			changeStr: outputPointCompare.changeStr,
			indicatorClass: outputPointCompare.indicatorClass,
			granulationLabel: outputPointCompare.granulationLabel,
			compareIndex: outputPointCompare.output?.dataRoot?.index,
			metricName: outputPointCompare.output?.metricName,
			goalData: outputPointCompare.extra,
		});
	}

	static createFromOutputPointOther(
		outputPointOther: DsDataPointOther,
		primaryPoint: DChartPrimaryPoint,
	): DChartComparePoint {
		return new DChartComparePoint({
			primaryPoint,
			value: outputPointOther.value,
			valueStr: outputPointOther.valueStr,
			changeStr: outputPointOther.changeStr,
			indicatorClass: outputPointOther.indicatorClass,
			granulationLabel: outputPointOther.output.granulationLabel,
		});
	}
}
